import { motion } from 'framer-motion';
import { Users, Eye, BarChart2, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';

const insights = [
  {
    icon: Eye,
    title: 'Parent Dashboard',
    description: 'Complete visibility into your child\'s learning patterns and performance trends.'
  },
  {
    icon: BarChart2,
    title: 'Learning Pattern Analysis',
    description: 'Identify strengths, weaknesses, and consistency trends over time.'
  },
  {
    icon: CheckCircle,
    title: 'Verify, Don\'t Nag',
    description: 'Stop asking "did you study?". Know exactly what was practiced and mastered without the daily argument.'
  }
];

export default function ForParentsSection() {
  const { login } = useAuth();

  return (
    <section 
      id="for-parents" 
      data-testid="for-parents-section"
      className="relative py-20 md:py-32 bg-card/50 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Visual - Parent Dashboard Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-2xl bg-card">
              {/* Parent Dashboard Mockup */}
              <div className="p-4 md:p-6 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-border">
                  <h3 className="text-lg font-semibold">Parent Dashboard</h3>
                  <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">Live</span>
                </div>

                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">RK</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm">Rahul Kumar</p>
                    <p className="text-xs text-muted-foreground">Class 10 • CBSE</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="p-3 rounded-lg bg-background border border-border">
                    <p className="text-xs text-muted-foreground">Overall</p>
                    <p className="text-xl font-bold text-primary">78%</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background border border-border">
                    <p className="text-xs text-muted-foreground">Trend</p>
                    <p className="text-xl font-bold text-green-500">+12%</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background border border-border">
                    <p className="text-xs text-muted-foreground">Papers</p>
                    <p className="text-xl font-bold">24</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">Subject Performance</p>
                  <div className="space-y-2">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>Mathematics</span>
                        <span className="text-primary font-mono">85%</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: '85%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>Physics</span>
                        <span className="text-primary font-mono">72%</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: '72%' }} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">Needs Attention</p>
                  <div className="flex flex-wrap gap-1">
                    <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-600 dark:text-red-400">Algebra</span>
                    <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-600 dark:text-red-400">Magnetism</span>
                  </div>
                </div>
              </div>
            </div>

            {/* STATIC FLOATING BADGE */}
            <div className="hidden sm:block absolute -bottom-4 -left-4 glass rounded-xl p-3 border border-primary/20 shadow-xl">
              <div className="flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium">Clear Insights</span>
              </div>
            </div>
          </motion.div>

          {/* Right Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2"
          >
            {/* STATIC BADGE */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Users className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">For Parents</span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
              Insights that matter.
              <br />
              <span className="text-primary">Beyond just marks.</span>
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Grades are just a snapshot. Edalyze is the live feed. Access a complete academic command center that tracks daily effort streaks, real-time syllabus completion, and hidden skill gaps. See exactly where they stand today, not just on exam day.
            </p>

            {/* STATIC INSIGHTS */}
            <div className="space-y-4 mb-8">
              {insights.map((insight, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex items-start gap-4 p-4 rounded-xl border ${
                    index === 0 
                      ? 'bg-primary/5 border-primary/30 ring-2 ring-primary/20' 
                      : 'bg-background border-border'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${index === 0 ? 'bg-primary/20' : 'bg-primary/10'}`}>
                    <insight.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-1 ${index === 0 ? 'text-primary' : ''}`}>
                      {insight.title}
                      {index === 0 && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-primary text-primary-foreground">Featured</span>}
                    </h4>
                    <p className="text-sm text-muted-foreground">{insight.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <a href="https://edalyze.app" target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                data-testid="parent-cta-btn"
                className="bg-primary text-primary-foreground shadow-lg"
              >
                Try Edalyze
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
